import { useState } from "react";
import { Video } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { Button } from "@/shared/ui/button";
import { PageHeader } from "@/shared/ui/PageHeader";
import { RecentWorksRow } from "@/entities/work/ui/RecentWorksRow";
import { DEPT_TITLE_KEY } from "@/entities/work/model/deptTitles";
import { VideoHubSections } from "../ui/components/VideoHubSections";
import { VideoPreviewDialog } from "../ui/components/VideoPreviewDialog";

/**
 * AI视频工作台 —— **纯卡墙**(2026-07-24 IA 二次收口 F1-b)。
 *
 * ═══ 这一页只回答一个问题:这个域能做什么、我选哪个 ═══════════════════════════
 * 原本这里还挂着一整套「进行中 / 草稿 / 历史(状态 chips + 场景 chips + 搜索 + 真分页)」——
 * 365 行里 17 处是历史。那是**检索**(从若干记录里找一条核对),与卡墙的**选择**
 * (从若干选项里挑一个开始做)是两种意图,挤在一页谁都不专心。
 *
 * 检索整体搬去 `/works`(唯一落点,三线聚合);这里只留一条「最近作品 · 查看全部 →」。
 * ★没有能力丢失:重命名 / 收藏 / 删除 / 重试 / 草稿恢复 / 在途轮询在 `/works` 全都在,
 * 且从三条线各一份收成了一份。
 *
 * 保留 `VideoPreviewDialog`:「列表=预览,详情=编辑」是这个仓既定的分工,
 * 最近作品带点成片仍然是先开轻量预览,不是直接跳进编辑页。
 */
export default function VideoHomePage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [previewTaskId, setPreviewTaskId] = useState<number | null>(null);

  return (
    <>
      <div className="w-full space-y-6">
        {/* 卡墙口径改了页头必须跟(这仓踩过的坑):现为 8 目的卡分两组(2026-09-12 三段式重排)。
            ★页头只留「资产库」:原来那两颗「自由创作 / 模版视频」的视觉权重压过整面墙 ——
            用户第一眼看到的是"自由创作",而不是"我要拍什么"。两条入口都没删,只是下移到
            它们该在的段落里(自由创作 → 「先来一条」大卡;模版 → 来源扁条「从模板开始」),
            与视觉舱 2026-07-25 收三个页头按钮是同一个判断。 */}
        <PageHeader
          icon={Video}
          title={t(DEPT_TITLE_KEY.video)}
          subtitle="先想清楚这条片拍来干什么 —— 目的一旦选定,整条片都按它编排。"
          actions={
            /* W3 资产库(动作/环境音)此前无入口只能手敲 URL —— 补在舱首页头部。 */
            <Button variant="outline" onClick={() => navigate("/ai-video/assets")}>
              资产库
            </Button>
          }
        />

        {/* 三段式:来源扁条 ×3 → 「先来一条」2 张 → 目的墙 8 卡分两组 */}
        <VideoHubSections />

        <RecentWorksRow
          lines={["video"]}
          onOpen={(w) => setPreviewTaskId(w.id)}
          onViewAll={(href) => navigate(href)}
        />
      </div>

      <VideoPreviewDialog
        taskId={previewTaskId}
        onClose={() => setPreviewTaskId(null)}
        onEnterDetail={(id) => navigate(`/ai-video/tasks/${id}`)}
      />
    </>
  );
}
